<script>
import {
  isPlainObject,
  get as getByPath,
  snakeCase,
  pick,
} from 'lodash';
import { validate as veeValidate } from 'vee-validate';

const regexp = /\{[^}^{]+\}/;

export default {
  inject: [
    'constants',
    'inputCharsLimiters',
    'helpers',
    'config',
    'productName',
    'isReturningUser',
  ],
  props: {
    formMetadata: {
      type: Object,
      default: null,
    },
    formMetadataPaths: {
      type: Object,
      default: null,
    },
    /* Session Data
    Journey data, loaded from show endpoint
    Is used to set initial values for question's fields
    */
    sessionData: {
      type: Object,
      default () { return {}; },
    },

    /* Question Id
    Unique question id
    If no `questionComponent` passed, `questionId` is used as a question's component name
    */
    questionId: {
      type: String,
      default: null,
    },

    /*
    If passed - it means that we are using basic question component
    */
    questionComponent: {
      type: String,
      default: null,
    },

    /* Step Id */
    stepId: {
      type: String,
      required: true,
    },

    /* Title
    Supports only text for now
    */
    title: {
      default: null,
      validator: value => (typeof value === 'string') || value === false,
    },
    titleAdditional: {
      default: null,
      validator: value => (typeof value === 'string') || value === false,
    },

    /* Subtitle
    Supports only text for now
    */
    subtitle: {
      default: null,
      validator: value => (typeof value === 'string') || value === false,
    },
    subtitleAdditional: {
      default: null,
      validator: value => (typeof value === 'string') || value === false,
    },

    /* Help Text
    Supports only text for now
    */
    helpText: {
      default: null,
      validator: value => ['string', 'object'].includes(typeof value) || value === false,
    },

    /* Custom Fields examples:
    For `base-radio-list`:
    { field: 'typeOfUse' }
    For unique component (like date-of-birth):
    { dateOfBirth: 'petDateOfBirth' }
    */
    customFields: {
      type: [Object, Array],
      default () { return {}; },
    },

    /* Validation Rules example:
    {
      [question's local fieldName]: {
        rule1: true,
        rule2: {
          rule2param: true
        }
      }
    }
    */
    validationRules: {
      type: Object,
      default: null,
    },

    /* Validation Errors */
    validationErrors: {
      type: Object,
      default: null,
    },

    /* Show Rules
    Key is a field name, value is an object of rules
    { [fieldName]: [vee-validate-rules] }
    */
    showRules: {
      type: Object,
      default: null,
    },

    /* Render After
    Array of fields to be set in order to render this question
    To check if the field is set we only check empty values: undefined, null and ''
    */
    renderAfter: {
      type: [Object, Array],
      default: null,
    },

    /*
      If value false, then if at least one condition is true, question will be rendered
    */
    renderConditionsCheckAll: {
      type: Boolean,
      default: true,
    },

    /* Default Values
    default values are set when question is rendered, fields are empty yet and no session data found
    default values are re-set when question renders again after `shouldQuestionRender` changes
    */
    defaultValues: {
      type: Object,
      default () { return {}; },
    },

    /* Hidden Values
    hidden values are set when question is hidden
    hidden values are re-set when question hides again after `shouldQuestionRender` changes
    */
    hiddenValues: {
      type: Object,
      default () { return {}; },
    },

    /* Hidden For Additionals */
    hiddenForAdditionals: {
      type: Boolean,
      default: false,
    },

    /* Only For Additionals */
    onlyForAdditionals: {
      type: Boolean,
      default: false,
    },

    /* Form Observer */
    formObserver: {
      type: Object,
      required: true,
    },

    /* Form Data */
    formData: {
      type: Object,
      required: true,
    },

    /* Step Data */
    stepData: {
      type: [Object, Array],
      required: true,
    },

    /* Step Shared Data
    Shared data between questions in step
    */
    stepSharedData: {
      type: Object,
      default: null,
    },

    /* Step Type */
    stepType: {
      default: 'single',
      validator: value => ['single', 'multiple'].includes(value), // TODO implement stepType from json perspective
    },

    /* Step Index */
    // Not used on Leadgen yet
    stepIndex: {
      type: [Number, String],
      default: null,
    },

    /*
    Options Config
    Used in base components
    { options, placeholder }
    */
    optionsConfig: {
      type: Object,
      default: null,
    },

    /*
    Prevent prefill for returning users
    */
    keepEmptyForReturningUsers: {
      type: Boolean,
      default: false,
    },

    /*
    Allow prevent prefill data from session locally from component
    */
    fromSession: {
      type: Boolean,
      default: true,
    },

    /*
    Prevent setting values for hideable questions on load.
    First use on driving school.
    Useful in case with duplicated fields which can reset each other on load.
    Example yearOfManufacture in driving school.
    */
    preventSetHideableValueOnLoad: {
      type: Boolean,
      default: false,
    },

    /**
     * Value will be set from sessionData in case when question was shown on load
     */
    isHideableValueSetFromSession: {
      type: Boolean,
      default: false,
    },

    /*
    Reset question fields if any of provided form fields changes
    Array - set of dependent fields, resets all questions fields
    [dependentField1, dependentField2, ...etc]
    Object - specify set of dependent fields per question field
    { field: [dependentField1, dependentField2, ...etc] }
    */
    resetAfterChanges: {
      type: [Array, Object],
      default: null,
    },
  },
  setup () {
    return {
      snakeCase,
    };
  },
  data () {
    return {
      // question data which will be emitted
      questionData: {},
      isShownOnLoad: false,

      // if show rules provided - is false until it's validated by veeValidate in watcher
      // if no show rules provided - is true always
      showRulesValidity: !this.showRules,

      renderAfterValidity: !this.renderAfter,

      getDataFromSession: this.fromSession,
    };
  },
  computed: {
    isMobile () { return this.$vuetify.breakpoint.smAndDown; },
    isAdditional () { return this.stepType === 'multiple' && this.stepIndex > 0; },
    isQuestionAvailable () {
      if (this.stepType !== 'multiple') return true;

      return (
        (!this.isAdditional && !this.onlyForAdditionals) ||
        (this.isAdditional && !this.hiddenForAdditionals)
      );
    },
    localCustomFields () {
      return Array.isArray(this.customFields) ? this.transformArrayToObj(this.customFields) : this.customFields || {};
    },

    // keys are local names for question fields
    // values are payload keys for question fields
    questionFields () {
      let fieldsAsObject = {};

      if (Array.isArray(this.fields)) {
        fieldsAsObject = this.fields.reduce((acc, field) => ({
          ...acc,
          [field]: field,
        }), {});
      }

      if (isPlainObject(this.fields)) {
        fieldsAsObject = this.fields;
      }

      return Object.assign({}, fieldsAsObject, this.localCustomFields);
    },

    shouldQuestionRender () {
      if (!this.isQuestionAvailable) return false;

      // return showRules vee validation result
      return this.showRulesValidity;
    },

    isRenderAfterFullfilled () {
      if (!this.renderAfter || this.renderAfter.length === 0) return true;
      if (Array.isArray(this.renderAfter)) {
        return !this.renderAfter.some(field => [undefined, null, ''].includes(this.stepData[field]));
      }

      return !!this.renderAfterValidity;
    },

    /* Dynamic Title
    If title contains `test {part} test`, {part} is replaced with a value
    {part} syntax: {stepId.questionId.questionfieldName}
    1st real usage is on Pet UI
    */
    // TODO:refactor - use this as a default title
    dynamicTitle () {
      if (!this.title) return null;

      let result = this.title;
      (this.title.match(regexp) || []).forEach(match => {
        const values = match
          .slice(1, -1) // removing brackets
          .replace(' ', '') // removing whitespaces
          .split('||'); // split values, where values[0] is a field path and values[1] is a fallback static text. Values[1] is optional

        const matchParts = values[0].split('.');
        const fallbackText = values[1] || match;
        const fieldName = matchParts.pop();
        const questionId = matchParts.pop();
        const stepId = matchParts.pop();
        const customFieldName = (() => {
          const configCustonFields = this.formMetadataPaths && this.formMetadataPaths[stepId][questionId]?.params?.customFields;
          const customFields = Array.isArray(configCustonFields) ? this.transformArrayToObj(configCustonFields) : configCustonFields;
          return customFields ? (customFields[fieldName] || fieldName) : fieldName;
        })();

        const matchValue = getByPath(this.formMetadata?.singleStep ? this.formData : this.stepData, customFieldName);

        // if pattern match but value is not found, check if value exist with direct pass
        // fit for core systems when related field value on a different step
        if (!matchValue) {
          let text = '';
          const questionData = getByPath(this.formData, `${stepId}.${questionId}` || match);
          if (Array.isArray(questionData)) {
            text = questionData.length > 1 ? fallbackText : questionData[0][fieldName];
          } else {
            text = getByPath(this.formData, match.slice(1, -1));
          }
          result = result.replace(match, text || fallbackText);
          return;
        }
        result = result.replace(match, matchValue || fallbackText);
      });
      return result;
    },

    baseWrapperProps () {
      const {
        shouldQuestionRender,
        isRenderAfterFullfilled,
        isAdditional,
      } = this;

      return {
        shouldQuestionRender,
        isRenderAfterFullfilled,
        isAdditional,
        ...this.$props,
        title: this.dynamicTitle || this.title,
      };
    },

    /**
     * Determine whether product should have column or row layout based on config or product name
     * @returns boolean
     */
    isColumnLayout () {
      return this.config.isVerticalSplitted;
    },
  },
  async created () {
    if (!this.isQuestionAvailable) return;

    this.initQuestionData();
    await this.initShowRulesValidity();
    await this.initRenderAfterValidity();

    this.$emit('render-after', this.isRenderAfterFullfilled);

    // Update inner fields if they are changed outside
    this.$watch(() => pick(this.stepData, Object.values(this.questionFields)),
      (updatedInnerFields, innerFields) => {
        if (JSON.stringify(updatedInnerFields) === JSON.stringify(innerFields)) return;
        this.questionData = updatedInnerFields;
      },
    );

    if (this.resetAfterChanges && Array.isArray(this.resetAfterChanges)) {
      this.$watch(
        () => this.resetAfterChanges.map(fieldToWatch => this.stepData[fieldToWatch]).join(''),
        async () => {
          // required for proper validation-provider update
          await this.$nextTick();
          Object.keys(this.questionFields).forEach(fieldName => {
            this.questionData[this.questionFields[fieldName]] = Object.prototype.hasOwnProperty.call(this.defaultValues, fieldName) ? this.defaultValues[fieldName] : null;
          });
        },
      );
    }

    if (this.renderAfter) {
      /*
      Question is hidden on load, if it has renderAfter
      It becomes visible after all other questions are initialized and renderAfter are valid
      If question has hiddenValues, these values will overwrite sessionValues once user manually hides and shows this question
      */
      this.$watch('isRenderAfterFullfilled', val => {
        this.$emit('render-after', val);
      });
    }

    if (this.showRules) {
      /*
      Question is hidden on load, if it has showRules
      It becomes visible after all other questions are initialized and showRules are valid
      If question has hiddenValues, these values will overwrite sessionValues once user manually hides and shows this question
      */

      if (!this.preventSetHideableValueOnLoad) {
        /*
        Need call right after init, when session data has already values it doesn't reset when question hide initially.
        */
        await this.setQuestionValue(this.shouldQuestionRender, this.getDataFromSession);
      }

      this.isShownOnLoad = this.shouldQuestionRender;

      this.$watch('shouldQuestionRender', val => {
        if (this.isHideableValueSetFromSession) {
          /**
           * Value will be taken from sessionData in case when question was shown on load.
           * First use on driving school in case with duplicated fields like yearOfManufacture.
           */
          this.getDataFromSession = this.isShownOnLoad;
        }

        this.setQuestionValue(val, this.getDataFromSession);
      });
    }
  },
  methods: {
    async setQuestionValue (shouldRender, getFromSession = true) {
      const fieldNamesSorted = Object.keys(this.questionFields).sort();
      const values = await Promise.all(fieldNamesSorted.map(fieldName => this.getFieldValue(fieldName, shouldRender, getFromSession)));
      fieldNamesSorted.forEach((fieldName, i) => {
        const fieldKey = this.questionFields[fieldName];
        this.questionData[fieldKey] = values[i];
      });
    },
    transformArrayToObj (array) {
      return array.reduce((acc, field) => ({
        ...acc,
        [field]: field,
      }), {});
    },
    getFieldErrors (fieldName, failedRules, fallbackErrors) {
      return this.helpers.retrieveFieldErrors(fieldName, failedRules, fallbackErrors, this);
    },
    getFieldValue (fieldName, shouldRender, getFromSession = true) {
      const fieldKey = this.questionFields[fieldName];

      let value;

      if (shouldRender) {
        let defaultValue = null;

        // Need for returning users (renewal links)
        // returning users (returningUser: true) don't get default values
        // new users (returningUser: false) get default values

        if (!this.isReturningUser || this.keepEmptyForReturningUsers === false) {
          defaultValue = Object.prototype.hasOwnProperty.call(this.defaultValues, fieldName) ? this.defaultValues[fieldName] : defaultValue;
        }

        if (![undefined, null, ''].includes(this.stepData[fieldKey])) {
          value = this.stepData[fieldKey];
        } else if (getFromSession) {
          // if sessionData property has undefined, null, '' - set defaultValue
          // in other case set property value from sessionData
          if ([undefined, null, ''].includes(this.sessionData[fieldKey])) {
            value = defaultValue;
          } else {
            value = this.sessionData[fieldKey];
          }
        } else {
          value = defaultValue;
        }
      } else {
        value = Object.prototype.hasOwnProperty.call(this.hiddenValues, fieldName) ? this.hiddenValues[fieldName] : null;
      }

      return value;
    },
    initQuestionData () {
      // initialize question data object
      this.questionData = this.createQuestionData();

      // Watch inner question data and emit if it changes
      this.$watch('questionData', val => {
        this.$emit('input', val);
      }, { deep: true });

      // Write question fields into payload
      this.$emit('input', this.questionData);
    },
    createQuestionData () {
      const data = {};
      const fieldNamesSorted = Object.keys(this.questionFields).sort();
      const values = fieldNamesSorted.map(fieldName => this.getFieldValue(fieldName, true, this.fromSession));
      fieldNamesSorted.forEach((fieldName, i) => {
        const fieldKey = this.questionFields[fieldName];
        data[fieldKey] = values[i];
      });
      return data;
    },
    async initShowRulesValidity () {
      this.showRulesValidity = await this.validateShowRules(this.showRules);

      this.$watch(
        () => Object.keys(this.showRules || {}).map(field => this.stepData[field]).join(''),
        async val => {
          this.showRulesValidity = await this.validateShowRules(this.showRules);
        }, { immediate: true },
      );
    },
    async initRenderAfterValidity () {
      this.renderAfterValidity = await this.validateRenderAfter(this.renderAfter, this.renderConditionsCheckAll);

      const renderAfterFields = Array.isArray(this.renderAfter) ? this.renderAfter : Object.keys(this.renderAfter || {});
      this.$watch(
        () => renderAfterFields.map(field => this.stepData[field]).join(''),
        async val => {
          this.renderAfterValidity = await this.validateRenderAfter(this.renderAfter, this.renderConditionsCheckAll);
        }, { immediate: true },
      );
    },
    async validateShowRules (showRules) {
      let rules = {};

      if (isPlainObject(showRules)) rules = showRules;
      if (Array.isArray(showRules)) {
        rules = showRules.reduce((acc, field) => ({
          ...acc,
          [field]: { required: true },
        }), {});
      }

      Object.keys(rules).forEach(fieldKey => {
        if (rules[fieldKey] === true) rules[fieldKey] = { required: true };
      });

      // Validate each field described in showRules
      const validity = await Promise.all(
        Object.keys(rules).map(field => {
          const { step, ...fieldRulesWithoutStep } = rules[field];
          const value = step ? this.formData[step]?.[field] : this.stepData[field];

          return veeValidate(value, fieldRulesWithoutStep);
        }),
      );

      return !validity.some(({ valid }) => valid === false);
    },
    async validateRenderAfter (showRules, renderConditionsCheckAll) {
      let rules = {};

      if (isPlainObject(showRules)) rules = showRules;
      if (Array.isArray(showRules)) {
        rules = showRules.reduce((acc, field) => ({
          ...acc,
          [field]: { required: true },
        }), {});
      }

      Object.keys(rules).forEach(fieldKey => {
        if (rules[fieldKey] === true) rules[fieldKey] = { required: true };
      });

      // Validate each field described in renderAfter
      const validity = await Promise.all(
        Object.keys(rules).map(field => {
          const { step, ...fieldRulesWithoutStep } = rules[field];

          const value = step ? this.formData[step]?.[field] : this.stepData[field];

          return veeValidate(value, fieldRulesWithoutStep);
        }),
      );

      if (!renderConditionsCheckAll) {
        return validity.filter(({ valid }) => valid === true).length;
      }

      return !validity.some(({ valid }) => valid === false);
    },
  },
};
</script>
